/** "Create payment schedule" wizard — additional GoCardless collections on the live mandate (recurring top-up or one-off),
 *  then an optional customer email with the combined table. Existing direct debit is never touched. */
import { useEffect, useState } from "react";
import { AlertTriangle, CalendarPlus, CheckCircle2, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtMoney } from "@/lib/regionFmt";
import { gcScheduleCreate, gcScheduleOptions, gcSchedulePreview } from "@/lib/api";
import { DetailsStep, MandateStep, TypeStep } from "./GcScheduleSteps";
import GcCombinedTable from "./GcCombinedTable";
import GcScheduleEmailStep from "./GcScheduleEmailStep";

const STEPS = ["Mandate", "Type", "Details", "Review", "Email"];
const fmtD = (s) => (s ? new Date(`${s.slice(0, 10)}T12:00:00`).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const errMsg = (e) => e?.response?.data?.detail || e.message;

const Stepper = ({ step }) => (
  <ol className="flex items-center gap-1 text-[10px] uppercase tracking-wide" data-testid="gc-wiz-stepper">
    {STEPS.map((s, i) => (
      <li key={s} className="flex items-center gap-1">
        <span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold ${i < step ? "bg-emerald-600 text-white" : i === step ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"}`}>{i < step ? "✓" : i + 1}</span>
        <span className={i === step ? "text-slate-900 font-semibold" : "text-slate-400"}>{s}</span>
        {i < STEPS.length - 1 && <span className="w-4 h-px bg-slate-300 mx-0.5" />}
      </li>
    ))}
  </ol>
);

const payloadFrom = (form, kind) => ({
  mandate_id: form.mandate_id, kind, amount: parseFloat(form.amount || 0), frequency: form.frequency, start_date: form.start_date || null,
  end_rule: form.end_rule, count: form.count ? parseInt(form.count, 10) : null, end_date: form.end_date || null, total: form.total ? parseFloat(form.total) : null,
  charge_date: form.charge_date || null, note: form.note || null,
});

export default function GcScheduleWizard({ caseRow, onClose, onDone }) {
  const [options, setOptions] = useState(null);
  const [step, setStep] = useState(0);
  const [kind, setKind] = useState("recurring");
  const [form, setForm] = useState({ mandate_id: "", amount: "", frequency: "business_daily", start_date: "", end_rule: "arrears", count: "", end_date: "", total: "", charge_date: "", note: "" });
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  useEffect(() => {
    gcScheduleOptions(caseRow.case_id).then((o) => {
      setOptions(o);
      const m = o.mandates.find((x) => x.mandate_id === o.default_mandate_id);
      setForm((f) => ({ ...f, mandate_id: o.default_mandate_id || "", start_date: m?.next_possible_charge_date || o.today, charge_date: m?.next_possible_charge_date || o.today, amount: o.arrears_now > 0 ? String(o.arrears_now.toFixed(2)) : "" }));
    }).catch((e) => toast.error(errMsg(e)));
  }, [caseRow.case_id]);

  useEffect(() => {
    if (step !== 3) return;
    setPreview(null); setPreviewErr(null);
    gcSchedulePreview(caseRow.case_id, payloadFrom(form, kind)).then(setPreview).catch((e) => setPreviewErr(errMsg(e)));
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickType = (k) => { setKind(k); if (k === "recurring" && options?.arrears_now > 0) setForm((f) => ({ ...f, amount: "" })); };
  const canNext = step === 0 ? !!form.mandate_id : step === 1 ? !!kind : step === 2 ? parseFloat(form.amount) > 0 : step === 3 ? !!preview : false;

  const create = async () => {
    setBusy(true);
    try {
      const r = await gcScheduleCreate(caseRow.case_id, payloadFrom(form, kind));
      setCreated(r);
      toast.success(kind === "one_off" ? "One-off payment created in GoCardless" : `Payment schedule created — ${r.schedule.instalments.length} payments`);
      setStep(4);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && (created ? onDone() : onClose())}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="gc-schedule-wizard">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><CalendarPlus className="w-4 h-4" />Create payment schedule <span className="text-xs font-normal text-muted-foreground">· {caseRow.customer_name} · {caseRow.loan_application_id}</span></DialogTitle>
        </DialogHeader>
        <Stepper step={step} />
        {!options ? <p className="text-xs text-muted-foreground flex items-center gap-1 py-6"><Loader2 className="w-3.5 h-3.5 animate-spin" />Checking mandates and arrears…</p> : (
          <div className="min-h-[240px]">
            {step === 0 && <MandateStep options={options} value={form.mandate_id} onChange={(v) => setForm((f) => ({ ...f, mandate_id: v }))} />}
            {step === 1 && <TypeStep value={kind} onChange={pickType} arrears={options.arrears_now} />}
            {step === 2 && <DetailsStep kind={kind} form={form} setForm={setForm} options={options} />}
            {step === 3 && (
              <div className="space-y-3" data-testid="gc-wiz-review-step">
                {previewErr && <div className="border border-red-300 bg-red-50 rounded p-2 text-xs text-red-800" data-testid="gc-wiz-preview-error"><AlertTriangle className="w-3.5 h-3.5 inline mr-1" />{previewErr}</div>}
                {!preview && !previewErr && <p className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" />Building the schedule…</p>}
                {preview && (
                  <>
                    <div className="border rounded p-3 bg-slate-50 text-sm" data-testid="gc-wiz-summary">
                      <p className="font-semibold">{preview.kind === "one_off" ? `One-off ${fmtMoney(preview.total, 2)} on ${fmtD(preview.first)}` : `${preview.count} × ${fmtMoney(preview.instalments[0]?.amount, 2)} ${options.frequencies.find((f) => f.key === form.frequency)?.label} · ${fmtMoney(preview.total, 2)} · ${fmtD(preview.first)} → ${fmtD(preview.last)}`}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">On the {preview.mandate.scheme_label} mandate {preview.mandate.mandate_id}. Existing direct debit{preview.subscriptions.length === 1 ? "" : "s"} ({preview.subscriptions.map((s) => `${fmtMoney(s.amount, 2)} ${s.frequency_label}`).join(", ") || "none"}) stay{preview.subscriptions.length === 1 ? "s" : ""} exactly as they are.</p>
                      {preview.warnings.map((w, i) => <p key={i} className="text-[11px] text-amber-800 mt-1" data-testid={`gc-wiz-warning-${i}`}><AlertTriangle className="w-3 h-3 inline mr-1" />{w}</p>)}
                    </div>
                    <GcCombinedTable rows={preview.combined} total={preview.total} kind={preview.kind} />
                  </>
                )}
              </div>
            )}
            {step === 4 && created && <GcScheduleEmailStep caseRow={caseRow} schedule={created.schedule} combined={created.combined} defaultIntro={created.default_intro} onFinished={() => onDone()} />}
          </div>
        )}
        {step < 4 && options && (
          <div className="flex items-center justify-between pt-2 border-t">
            <Button variant="ghost" onClick={step === 0 ? onClose : () => setStep((s) => s - 1)} data-testid="gc-wiz-back-btn"><ChevronLeft className="w-4 h-4 mr-1" />{step === 0 ? "Cancel" : "Back"}</Button>
            {step < 3 ? (
              <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext} data-testid="gc-wiz-next-btn">Next<ChevronRight className="w-4 h-4 ml-1" /></Button>
            ) : (
              <Button onClick={create} disabled={!preview || busy} className="bg-slate-900 hover:bg-slate-800" data-testid="gc-wiz-create-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}Create in GoCardless</Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
